import { RpcRequest, RpcResponse, RpcInvalidEndpoint } from './common'
import getMessageOrigin from '../../utils/getMessageOrigin'

export interface RpcServerEndpoints {
  [name: string]: Function
}

export class RpcServer {
  private window: Window

  private namespace: string

  private endpoints: RpcServerEndpoints = {}

  private interstitialOpener: {
    win: Window
    origin: string
  }

  public constructor(window: Window, namespace: string) {
    this.window = window
    this.namespace = namespace
  }

  public addEndpoints(endpoints: RpcServerEndpoints) {
    this.endpoints = {
      ...this.endpoints,
      ...endpoints
    }
  }

  public listen() {
    this.window.addEventListener('message', (event: MessageEvent) => this.handleMessage(event))
    this.window.addEventListener('message', (event: MessageEvent) => this.handleHostedInterstitialMessage(event))
  }

  private handleHostedInterstitialMessage(event: MessageEvent) {
    // eslint-disable-next-line no-shadow
    enum RequestSources {
      LEARN_MORE = 'LEARN_MORE',
    }
    // eslint-disable-next-line no-shadow
    enum RequestTypes {
      CLOSE = 'CLOSE',
      COMPLETE ='COMPLETE',
      SUCCESSFUL = 'SUCCESSFUL'
    }
    if (event.data === null) {
      return
    }

    let request: { source: RequestSources; type: RequestTypes }
    try {
      request = JSON.parse(event.data)
    } catch (ex) {
      return
    }

    //  We have 2 listeners that both are listening to the same event,
    // the assumption is that handleMessage will fire before handleHostedInterstitialMessage
    // and set the this.interstitialOpener, this is true in most case but if someone
    // open and closes the interstitial fast enough this will throw error
    // @TODO this is a temporary solution, to avoid refactoring this behavior at the moment
    if (!this.interstitialOpener) {
      return
    }

    const origin = getMessageOrigin(this.interstitialOpener.origin)

    if (request.source === RequestSources.LEARN_MORE && request.type === RequestTypes.CLOSE) {
      const endpoint = 'closeHostedInterstitial'
      this.interstitialOpener.win.postMessage(endpoint, origin)
      this.endpoints[endpoint](request)
    }
  }

  private handleMessage(event: MessageEvent) {
    if (event.data === null) {
      return
    }

    let request: RpcRequest
    try {
      request = JSON.parse(event.data)
    } catch (ex) {
      return
    }

    if (request.namespace !== this.namespace || !request.id) {
      return
    }

    const response: RpcResponse = {
      id: request.id
    }

    if (
      request.endpoint === 'openHostedInterstitial'
    ) {
      this.interstitialOpener = {
        win: event.source as WindowProxy,
        origin: event.origin
      }
    }

    try {
      if (!(request.endpoint in this.endpoints)) {
        throw new RpcInvalidEndpoint(`Endpoint "${request.endpoint}" not exist`)
      }

      response.result = this.endpoints[request.endpoint].apply(this, [...request.arguments, event])
    } catch (ex) {
      response.error = {
        name: ex.name,
        message: ex.message
      }
    }

    if (!event.source) {
      return
    }

    const origin = getMessageOrigin(event.origin)

    if ((typeof response.result) === 'function') {
      response.result((result: any) => {
        (event.source as WindowProxy).postMessage(JSON.stringify({ ...response, result }), origin)
      })
    } else {
      (event.source as WindowProxy).postMessage(JSON.stringify(response), origin)
    }
  }
}
